import { ActionTree } from 'vuex';
import BookingState from '@/types/store/booking/BookingState';
import RootState from '@/types/store/booking/RootState';
import CommuneProjection from '@/types/projections/CommuneProjection';
import Booking from '@/types/taxcard/Booking';
import BookingPriceProjection from '@/types/projections/BookingPriceProjection';
import BIObject from '@/types/taxcard/BIObject';
import PriceDiscountOptionProjection from '@/types/projections/PriceDiscountOptionProjection';
import AnnualCandidateProjection from '@/types/projections/AnnualCandidateProjection';
import AccommodationProjection from '@/types/projections/AccommodationProjection';
import { cloneDeep } from 'lodash';
import CommuneApi from '@/app/api/CommuneApi';
import BookingApi from '@/app/api/BookingApi';
import BookingResponse from '@/types/taxcard/BookingResponse';
import AccommodationApi from '@/app/api/AccommodationApi';
import moment from 'moment';
import BookingDonationResponse from '@/types/taxcard/BookingDonationResponse';
import { GuestTypesEnum } from '@/types/taxcard/GuestType';
import APIBooking from '@/types/taxcard/APIBooking';

const getCleanBookingClone = (booking: Booking): APIBooking => {
  const bookingClone: any = cloneDeep(booking);
  const apiBookingClone: any = new APIBooking(bookingClone.communeId, [], bookingClone.strictAccommodation, bookingClone.communeMailConsent);
  // bookingClone.bIObjects = bookingClone.bIObjects.map((bIObject: BIObject) => {
  //   const {bIObjectData, ...otherBIObjectProps} = bIObject;
  //   const t: any = { ...bIObjectData }
  //   if (t.guestType === GuestTypesEnum.day) {
  //     t.addressStreetAndNumber = '';
  //     t.addressCity = '';
  //     t.addressZipCode = '';
  //     t.accommodation = {};
  //   } else {
  //     t.isAnonymous = false;
  //   }
  //   if (t.isAnonymous) {
  //     t.salutation = 'MR';
  //     t.firstName = '';
  //     t.lastName = '';
  //     t.cellphoneNumber = '';
  //     t.isAnnual = false;
  //   }
  //   t.birthday = t.birthday ? t.birthday.toISOString() : null;
  //   return t;
  // });
  
  for (let ind1 = 0; ind1 < bookingClone.bIObjects.length; ind1++) {
    const bIObject = bookingClone.bIObjects[ind1];
    const {bIObjectData, ...otherBIObjectProps} = bIObject;
    const finalBIObjectData: any = { ...bIObjectData }
    if (finalBIObjectData.guestType === GuestTypesEnum.day) {
      finalBIObjectData.addressStreetAndNumber = '';
      finalBIObjectData.addressCity = '';
      finalBIObjectData.addressZipCode = '';
      finalBIObjectData.accommodation = {};
    } else {
      finalBIObjectData.isAnonymous = false;
    }
    if (finalBIObjectData.isAnonymous) {
      finalBIObjectData.salutation = 'MR';
      finalBIObjectData.firstName = '';
      finalBIObjectData.lastName = '';
      finalBIObjectData.cellphoneNumber = '';
      finalBIObjectData.isAnnual = false;
    }
    finalBIObjectData.birthday = finalBIObjectData.birthday ? finalBIObjectData.birthday.toISOString() : null;
    apiBookingClone.bIObjects.push(finalBIObjectData);
  }

  return apiBookingClone;
};

const config = {
  withCredentials: true,
};

const withTimeout = (promise: Promise<any>, timeout: number = 7000, exception: Symbol = Symbol()): Promise<any> => {
  let timer: number;
  return Promise.race([
    promise,
    new Promise((_resolve, reject) => (timer = setTimeout(reject, timeout, exception))),
  ]).finally(() => clearTimeout(timer));
};

const errorOverlays = {
  timeout: {
    title: 'Ladezeit Fehler',
    message: 'Entschuldigung. Die Anfrage hat leider zu lange gedauert.',
  },
  other: {
    title: 'Unbekannter Fehler',
    message: 'Entschuldigung. Hier ist etwas schiefgelaufen.',
  },
};

const createTransientCardsIfNecessary = (booking: APIBooking) => {
  let hasTransientCards = false;
  let transientCardCount = 0;
  if (booking.isAnonymous && (booking.bIObjects[0].personCount > 1 || booking.bIObjects[0].childCount >= 1)) {
    hasTransientCards = true;
    const desiredChildrenCount = parseInt(booking.bIObjects[0].childCount + '');
    const desiredPersonCount = booking.bIObjects[0].personCount;
    booking.bIObjects[0].personCount = 1;
    booking.bIObjects[0].childCount = 0;
    for (let i = 0; i < desiredPersonCount - 1; i++) {
      const bIObject = cloneDeep(booking.bIObjects[0]);
      bIObject.petCount = 0;
      booking.bIObjects.push(bIObject);
    }
    for (let i = 0; i < desiredChildrenCount; i++) {
      const bIObject = cloneDeep(booking.bIObjects[0]);
      bIObject.personCount = 0;
      bIObject.childCount = 1;
      bIObject.birthday = moment.utc().subtract(10, 'years');
      booking.bIObjects.push(bIObject);
    }
  }
  if (!booking.isAnonymous && booking.bIObjects[0].guestType === GuestTypesEnum.overnight && booking.bIObjects[0].personCount > 0) {
    hasTransientCards = true;
    const desiredPersonCount = parseInt(booking.bIObjects[0].personCount + '');
    booking.bIObjects[0].personCount = 0;
    const transientCards = [];
    for (let i = 0; i < desiredPersonCount; i++) {
      const bIObject = cloneDeep(booking.bIObjects[0]);
      bIObject.petCount = 0;
      bIObject.firstName = `Teilnehmer ${i + 1}`;
      bIObject.option = bIObject.groupOption;
      transientCards.push(bIObject);
      transientCardCount += 1;
    }
    booking.bIObjects.splice(1, 0, ...transientCards);
  }
  return { booking, hasTransientCards, transientCardCount };
};

const actions: ActionTree<BookingState, RootState> = {
  fetchCommunes({ commit, rootState }): Promise<CommuneProjection[]> {
    return CommuneApi.getCommuneList().then(data => {
      commit('setCommunes', data);
      return data;
    });
  },
  fetchCommune({ commit, rootState }, id: number): Promise<CommuneProjection> {
    return CommuneApi.getCommune(id);
  },
  fetchCommuneDiscounts({ commit, rootState }, { communeId, bIObject }): Promise<PriceDiscountOptionProjection[]> {
    return CommuneApi.getCommuneDiscountsForTaxcard(communeId, bIObject);
  },
  async fetchBookingPrice({ commit, rootState }, { booking }: { booking: Booking }): Promise<BookingPriceProjection> {
    const data = getCleanBookingClone(booking);
    (data as any).customerUUID = rootState.customerUUID;
    const { hasTransientCards, booking: transientBooking, transientCardCount } = createTransientCardsIfNecessary(data);
    const response = await BookingApi.getBookingPrice(transientBooking);
    if (hasTransientCards) {
      response.taxcards = response.taxcards.reduce((acc, val, index) => {
        const i = index <= transientCardCount ? 0 : index - transientCardCount;
        if (!acc[i]) {
          acc[i] = val;
        } else {
          acc[i].price += val.price;
          acc[i].petPrice += val.petPrice;
        }
        return acc;
      }, [] as BookingPriceProjection['taxcards']);
    }
    return response;
  },
  bookingRequest({ commit, rootState }, { booking }: { booking: Booking }): Promise<BookingResponse | void> {
    const data = getCleanBookingClone(booking);
    (data as any).customerUUID = rootState.customerUUID;
    const { booking: transientBooking } = createTransientCardsIfNecessary(data);
    const timeoutError = Symbol();
    return withTimeout(BookingApi.bookTaxcards(transientBooking), 20000, timeoutError).catch(error => {
      const overlayData = error === timeoutError ? errorOverlays.timeout : errorOverlays.other;
      commit('openOverlay', { overlayName: 'Info', overlayData: overlayData }, { root: true });
    });
  },
  donationRequest({ commit, rootState }, { booking }: { booking: Booking }): Promise<BookingDonationResponse | void> {
    const data = getCleanBookingClone(booking);
    (data as any).customerUUID = rootState.customerUUID;
    const timeoutError = Symbol();
    return withTimeout(BookingApi.donate(data), 20000, timeoutError).catch(error => {
      const overlayData = error === timeoutError ? errorOverlays.timeout : errorOverlays.other;
      commit('openOverlay', { overlayName: 'Info', overlayData: overlayData }, { root: true });
    });
  },  
  fetchAnnualCandidate(
    { commit, rootState },
    { communeId, bIObject }: { communeId: number; bIObject: BIObject }
  ): Promise<AnnualCandidateProjection> {
    return CommuneApi.getAnnualCandidateStatus(communeId, bIObject);
  },
  resetSearchAccommodations({ commit }) {
    commit('setAccommodations', []);
  },
  searchAccommodations({ state, commit, rootState }, { query }): Promise<AccommodationProjection[]> {
    let currentBooking = state.currentBooking;
    if (!currentBooking) {
      return Promise.resolve([]);
    } else {
      const communeId = currentBooking.communeId;
      return AccommodationApi.searchAccommodations(communeId, query).then(data => {
        commit('setAccommodations', data);
        return data;
      });
    }
  },
  fetchAccommodation({ state, commit, rootState }, { communeId, uid }): Promise<AccommodationProjection> {
    return AccommodationApi.fetchAccommodation(communeId, uid);
  },
  resetBooking({ state, commit }): void {
    localStorage.removeItem('currentBooking');
    localStorage.removeItem('currentPriceProjection');
    localStorage.removeItem('currentOrderId');
    localStorage.removeItem('currentBookingSession');

    state.currentBooking = null;
    state.currentBookingSession = null;
    state.currentPriceProjection = null;
    state.currentOrderId = null;
    commit('setAccommodations', []);
  },
};

export default actions;
